import React, { useEffect, useState } from 'react';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { MOCK_VEHICLES, Vehicle } from '../utils/mockData';
import { format, differenceInMinutes } from 'date-fns';
import { AlertTriangle, Filter, ArrowUpDown } from 'lucide-react';
import { cn } from '../utils/cn';
interface TruckTrackerProps {
  onVehicleClick: (vehicle: Vehicle) => void;
}
export function TruckTracker({
  onVehicleClick
}: TruckTrackerProps) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000); // Update every minute
    return () => clearInterval(timer);
  }, []);
  const getDuration = (entryTime: Date) => {
    const diff = differenceInMinutes(now, entryTime);
    const hours = Math.floor(diff / 60);
    const mins = diff % 60;
    return `${hours}h ${mins}m`;
  };
  const getSLAStatus = (entryTime: Date) => {
    const diff = differenceInMinutes(now, entryTime);
    if (diff > 240) return 'Overdue';
    if (diff > 180) return 'Approaching';
    return 'Under 3h';
  };
  const getSLAColor = (status: string) => {
    switch (status) {
      case 'Overdue':
        return 'danger';
      case 'Approaching':
        return 'warning';
      default:
        return 'success';
    }
  };
  const vehiclesInside = MOCK_VEHICLES.filter(v => v.status === 'Inside');
  return <Card className="h-full flex flex-col" noPadding data-id="element-318">
      <div className="border-b border-gray-800 p-4 flex justify-between items-center" data-id="element-319">
        <h2 className="text-lg font-semibold text-white" data-id="element-320">
          Trucks Inside — Time Since Entry
        </h2>
        <div className="flex gap-2" data-id="element-321">
          <button className="p-2 text-gray-400 hover:text-white rounded-md hover:bg-gray-800" data-id="element-322">
            <Filter className="h-4 w-4" data-id="element-323" />
          </button>
        </div>
      </div>

      <div className="overflow-x-auto" data-id="element-324">
        <table className="w-full text-left text-sm" data-id="element-325">
          <thead className="bg-gray-900/50 text-gray-400" data-id="element-326">
            <tr data-id="element-327">
              <th className="px-4 py-3 font-medium" data-id="element-328">Plate Number</th>
              <th className="px-4 py-3 font-medium" data-id="element-329">Type</th>
              <th className="px-4 py-3 font-medium" data-id="element-330">Entry Time</th>
              <th className="px-4 py-3 font-medium" data-id="element-331">Time Inside</th>
              <th className="px-4 py-3 font-medium" data-id="element-332">SLA Status</th>
              <th className="px-4 py-3 font-medium" data-id="element-333">Location</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800" data-id="element-334">
            {vehiclesInside.map(vehicle => {
            const slaStatus = getSLAStatus(vehicle.entryTime);
            return <tr key={vehicle.id} onClick={() => onVehicleClick(vehicle)} className="group cursor-pointer hover:bg-gray-800/50 transition-colors" data-id="element-335">
                  <td className="px-4 py-3 font-medium text-white group-hover:text-amber-500" data-id="element-336">
                    <div className="flex items-center gap-2" data-id="element-337">
                      {vehicle.plate}
                      {vehicle.hasWarning && <AlertTriangle className="h-3 w-3 text-amber-500" data-id="element-338" />}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-300" data-id="element-339">{vehicle.type}</td>
                  <td className="px-4 py-3 text-gray-400" data-id="element-340">
                    {format(vehicle.entryTime, 'HH:mm')}
                  </td>
                  <td className="px-4 py-3 font-mono text-gray-300" data-id="element-341">
                    {getDuration(vehicle.entryTime)}
                  </td>
                  <td className="px-4 py-3" data-id="element-342">
                    <Badge variant={getSLAColor(slaStatus)} data-id="element-343">{slaStatus}</Badge>
                  </td>
                  <td className="px-4 py-3 text-gray-400" data-id="element-344">
                    {vehicle.bay || vehicle.gate}
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
    </Card>;
}